import { useState, useEffect, useRef } from 'react'
import {
  Container,
  Progress,
  Wrapper,
  Clock,
  Time
} from './styles'
import ClockPng from '../../assets/images/defaults/clock.png'
import { secondsToMinutes } from '../../utils/time_conversor'
import { useGame, useGameWrapper } from '../../contexts'

type Props = {
  timeInSeconds: number;
  className?: string;
  paused?: boolean;
}

const Timer = ({ timeInSeconds, className, paused = false }: Props) => {
  const [seconds, setSeconds] = useState(0)
  const [canActivateTimeout, setCanActivateTimeout] = useState(false)
  const progressRef = useRef<HTMLCanvasElement | null>(null)
  const { game } = useGame()
  const { setTimeout: updateTimeout, timeout, timePaused, currentMission } = useGameWrapper()

  function decrementSeconds () {
    if (seconds === 0) {
      return
    }
    setSeconds(state => state - 1)
  }

  function loadData () {
    setSeconds(timeInSeconds)
  }

  function loadCanvas () {
    const percent = (100 * seconds) / timeInSeconds
    const canvas = progressRef.current
    if (canvas !== null) {
      const ctx = canvas.getContext('2d')
      if (ctx !== null) {
        draw(ctx, canvas, percent)
      }
    }
  }

  function draw (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, percent: number) {
    ctx.lineWidth = 10
    ctx.strokeStyle = '#32CD32'

    const offsetX = 5
    const offsetY = 5
    const horizLineLength = 270
    const vertLineLength = 115
    const cornerRadius = 8

    const cornerLength = 5 * cornerRadius * Math.PI
    const totalLength = cornerLength * 4 + horizLineLength * 2 + vertLineLength * 2

    const startT = 0
    const startTR = horizLineLength
    const startR = startTR + cornerLength
    const startBR = startR + vertLineLength
    const startB = startBR + cornerLength
    const startBL = startB + horizLineLength
    const startL = startBL + cornerLength
    const startTL = startL + vertLineLength

    let d, x1, x2, y1, y2, x, y, start, end

    const accumLength = percent / 100 * totalLength

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    d = accumLength - startT
    d = Math.min(d, horizLineLength)
    if (d > 0) {
      x1 = offsetX + cornerRadius
      y1 = offsetY
      x2 = offsetX + cornerRadius + d
      y2 = offsetY
      drawLine(ctx, x1, y1, x2, y2)
    }

    d = accumLength - startTR
    d = Math.min(d, cornerLength)
    if (d > 0) {
      x = offsetX + cornerRadius + horizLineLength
      y = offsetY + cornerRadius
      start = -Math.PI / 2
      end = -Math.PI / 2 + (d / cornerLength * Math.PI / 2)
      drawCorner(ctx, x, y, start, end, cornerRadius)
    }

    d = accumLength - startR
    d = Math.min(d, vertLineLength)
    if (d > 0) {
      x1 = offsetX + cornerRadius + horizLineLength + cornerRadius
      y1 = offsetY + cornerRadius
      x2 = offsetX + cornerRadius + horizLineLength + cornerRadius
      y2 = offsetY + cornerRadius + d
      drawLine(ctx, x1, y1, x2, y2)
    }

    d = accumLength - startBR
    d = Math.min(d, cornerLength)
    if (d > 0) {
      x = offsetX + cornerRadius + horizLineLength
      y = offsetY + cornerRadius + vertLineLength
      start = 0
      end = (d / cornerLength) * Math.PI / 2
      drawCorner(ctx, x, y, start, end, cornerRadius)
    }

    d = accumLength - startB
    d = Math.min(d, horizLineLength)
    if (d > 0) {
      x1 = offsetX + cornerRadius + horizLineLength
      y1 = offsetY + cornerRadius + vertLineLength + cornerRadius
      x2 = offsetX + cornerRadius + horizLineLength - d
      y2 = offsetY + cornerRadius + vertLineLength + cornerRadius
      drawLine(ctx, x1, y1, x2, y2)
    }

    d = accumLength - startBL
    d = Math.min(d, cornerLength)
    if (d > 0) {
      x = offsetX + cornerRadius
      y = offsetY + cornerRadius + vertLineLength
      start = Math.PI / 2
      end = Math.PI / 2 + (d / cornerLength) * Math.PI / 2
      drawCorner(ctx, x, y, start, end, cornerRadius)
    }

    d = accumLength - startL
    d = Math.min(d, vertLineLength)
    if (d > 0) {
      x1 = offsetX
      y1 = offsetY + cornerRadius + vertLineLength
      x2 = offsetX
      y2 = offsetY + cornerRadius + vertLineLength - d
      drawLine(ctx, x1, y1, x2, y2)
    }

    d = accumLength - startTL
    d = Math.min(d, cornerLength)
    if (d > 0) {
      x = offsetX + cornerRadius
      y = offsetY + cornerRadius
      start = Math.PI
      end = Math.PI + (d / cornerLength) * Math.PI / 2
      drawCorner(ctx, x, y, start, end, cornerRadius)
    }
  }

  function drawLine (ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  }

  function drawCorner (ctx: CanvasRenderingContext2D, x: number, y: number, start: number, end: number, cornerRadius: number) {
    ctx.beginPath()
    ctx.arc(x, y, cornerRadius, start, end, false)
    ctx.stroke()
  }

  useEffect(() => {
    loadData()
  }, [timeInSeconds, timePaused])

  useEffect(() => {
    if (seconds > 0 && !timePaused) {
      if (timeout) updateTimeout(false)
      const timer = setTimeout(() => decrementSeconds(), 1000)

      if (seconds === 1) setCanActivateTimeout(true)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [seconds, timePaused, currentMission])

  useEffect(() => {
    loadCanvas()

    if (seconds === 0 && canActivateTimeout) {
      updateTimeout(true)
      setCanActivateTimeout(false)
    }
  }, [seconds, currentMission])

  return (
    <Container className={className}>
      <Progress ref={progressRef} />
      <Wrapper>
        <Clock src={ClockPng}/>
        <Time style={{ color: game?.textColor || '#fff' }}>{ secondsToMinutes(seconds) }</Time>
      </Wrapper>
    </Container>
  )
}

export default Timer
